<script>
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {ref, watch} from "vue";
import {useRecruitmentSourcingStore} from "@/stores/Recruitment/RecruitmentSourcing.js";
import RecruitmentRequestFilter from "@/views/Request/RecruitmentRequest/Components/RecruitmentRequestFilter.vue";
import axios from "axios";

export default {
    name: "Sourcings",
    components: {RecruitmentRequestFilter},
    setup() {
        const {loading, total, itemsPerPage, filter} = useSharedData();

        const {
            transformFilterValues,
            formatedDate,
            getStatusColor
        } = useSharedMethod();

        const {$i18n, $router} = useTools();

        const recruitmentSourcingStore = useRecruitmentSourcingStore();

        const headers = [
            {
                text: "...",
                sortable: false,
                align: "left",
                value: "action"
            },
            {
                text: $i18n.t("common.code"),
                align: "left",
                value: "requestCode",
                sortable: true
            },
            {
                text: $i18n.t("recruitment_request.occupation"),
                align: "left",
                value: "requestOccupationName",
                sortable: true
            },
            {
                text: $i18n.t("recruitment_request.recruitment_date"),
                align: "left",
                value: "requestExpectedDate",
                sortable: true
            },
            {
                text: $i18n.t("recruitment_request.requesting_agency"),
                align: "left",
                value: "requestRequestingAgencyName",
                sortable: true
            },
            {
                text: $i18n.t("recruitment_request.applicant"),
                align: "left",
                value: "requestWorkerLastName",
                sortable: true
            },
            {
                text: $i18n.t("recruitment_request.post_number"),
                align: "left",
                value: "requestPostNumber",
                sortable: true
            },
            {
                text: $i18n.t("recruitment_request.cv_number"),
                align: "left",
                value: "size",
                sortable: true
            },
            {
                text: $i18n.t("recruitment_request.share"),
                align: "left",
                value: "shared",
                sortable: true
            },
            {
                text: $i18n.t("recruitment_request.receive_cv"),
                align: "left",
                value: "closed",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("recruitment_request.state"),
                align: "left",
                value: "requestState",
                sortable: true,
                width: "0%"
            }
        ];

        const actions = [
            {
                title: $i18n.t("common.lock"),
                icon: "mdi-lock",
                color: "grey darken-2",
                access: item => item.updateOption && !item.closed,
                disabled: item => item.requestState === "CLOSED",
                func: item => {
                    $router.push({
                        name: "recruitment_sourcings.lock",
                        params: {id: item.id}
                    });
                }
            },
            {
                title: $i18n.t("common.unlock"),
                icon: "mdi-lock-open-variant",
                color: "grey darken-2",
                access: item => item.updateOption && item.closed,
                disabled: item => item.requestState === "CLOSED",
                func: item => {
                    $router.push({
                        name: "recruitment_sourcings.unlock",
                        params: {id: item.id}
                    });
                }
            },
            {
                title: $i18n.t("recruitment_request.share"),
                icon: "mdi-share",
                color: "blue darken-2",
                access: item => item.updateOption,
                disabled: () => false,
                func: item => {
                    $router.push({
                        name: "recruitment_sourcings.share",
                        params: {id: item.id}
                    });
                }
            },
            {
                title: $i18n.t("recruitment_request.upload_cv"),
                icon: "mdi-upload-multiple",
                color: "teal",
                access: item => item.updateOption,
                disabled: () => false,
                func: item => {
                    $router.push({
                        name: "recruitment_sourcings.add",
                        params: {id: item.id}
                    });
                }
            },
            {
                title: $i18n.t("recruitment_request.plan"),
                icon: "mdi-calendar-clock",
                color: "orange",
                access: item => item.savePlanningOption,
                disabled: item => item.requestState === "CLOSED",
                func: item => {
                    $router.push({
                        name: "recruitment_sourcings.planning.add",
                        params: {id: item.id}
                    });
                }
            }
        ];

        const searchParams = ref({
            sortBy: "requestCode",
            descending: true,
            page: 1,
            itemsPerPage: itemsPerPage
        });

        function handleSearch(params) {
            loading.value = true;
            recruitmentSourcingStore.setRecruitmentSourcings([]);
            total.value = 0;

            loading.value = true;
            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;

            axios
                .get("/recruitmentSourcings", {
                    params: transformFilterValues(filter.value)
                })
                .then(response => {
                    recruitmentSourcingStore.setRecruitmentSourcings(
                        response.data.elements
                    );
                    total.value = response.data.total;
                })
                .finally(() => {
                    loading.value = false;
                });
        }

        function findAll() {
            handleSearch(searchParams.value);
        }

        watch(
            () => filter.value,
            () => {
                searchParams.page = 1;
                findAll();
            },
            {deep: true, immediate: true}
        );

        watch(
            searchParams,
            newParams => {
                handleSearch(newParams);
            },
            {deep: true}
        );

        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                filter.value[key] = null;
            });
        }

        function view(item) {
            $router.push({
                name: "recruitment_sourcings.view",
                params: {id: item.requestId}
            });
        }

        return {
            recruitmentSourcingStore,
            headers,
            actions,
            loading,
            total,
            itemsPerPage,
            filter,
            searchParams,
            findAll,
            clearFilter,
            view,
            formatedDate,
            getStatusColor
        };
    }
};
</script>

<template>
    <div>
        <w-app
            :title="$t('module.recruitment_sourcing')"
            show-total
            :total="total"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <RecruitmentRequestFilter v-model="filter"/>
            </template>

            <template v-slot:rightToolbar>
                <w-btn-reload :loading="loading" @press="findAll"/>
            </template>

            <template v-slot:content>
                <w-server-data-table
                    v-model="searchParams"
                    :actions="actions"
                    :headers="headers"
                    :items="recruitmentSourcingStore.recruitmentSourcings"
                    :loading="loading"
                    :total="total"
                    root-path="recruitment_sourcings"
                    module="RECRUITMENT_SOURCING"
                    @rowDbClick="view"
                >
                    <template v-slot:item.requestCode="{ item }">
                        <w-chip :text="item.requestCode"/>
                    </template>
                    <template v-slot:item.requestOccupationName="{ item }">
                        <w-inline-text bold :text="item.requestOccupationName"/>
                    </template>
                    <template v-slot:item.requestExpectedDate="{ item }">
                        <w-chip
                            color="teal"
                            :text="formatedDate(item.requestExpectedDate)"
                        />
                    </template>
                    <template v-slot:item.requestWorkerLastName="{ item }">
                        <w-inline-text :text="item.requestWorkerName"/>
                    </template>
                    <template v-slot:item.requestRequestingAgencyName="{ item }">
                        <w-inline-text :text="item.requestRequestingAgencyName"/>
                    </template>

                    <template v-slot:item.requestPostNumber="{ item }">
                        <w-chip outlined :text="item.requestPostNumber"/>
                    </template>
                    <template v-slot:item.size="{ item }">
                        <w-chip outlined :text="item.size"/>
                    </template>
                    <template v-slot:item.shared="{ item }">
                        <v-icon :color="item.shared ? 'teal' : 'red'">mdi-check-decagram</v-icon>
                    </template>
                    <template v-slot:item.closed="{ item }">
                        <w-chip
                            v-if="item.closed"
                            color="grey darken-2"
                            :text="$t('common.locked')"
                        />
                        <w-chip v-else color="blue" text="En Cours"/>
                    </template>
                    <template v-slot:item.requestState="{ item }">
                        <w-chip
                            :color="getStatusColor(`RECRUITMENT_${item.requestState}`)"
                            :text="$t(`recruitment_request.${item.requestState}`)"
                        />
                    </template>
                    <template v-slot:item.requestCloseDate="{ item }">
                        <w-inline-text :text="formatedDate(item.requestCloseDate)"/>
                    </template>
                </w-server-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>